import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Lightbulb,
  Activity,
  Wallet,
  Route,
  LayoutTemplate,
  MessageCircle,
  Gauge,
  BookOpen,
  Users,
  KanbanSquare,
  Truck,
  Images,
  CalendarDays,
  CalendarRange,
  Newspaper,
  Library,
  Network,
  Camera,
  Type,
  GraduationCap,
  Sparkles,
  FileText,
  LogOut,
  Palette,
  Sunrise,
  TrendingUp,
  PenLine,
  Wrench,
  Brain,
  BookMarked,
  FlaskConical,
  Mail,
  ClipboardList,
  MessagesSquare,
  Link2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

/** Menu "Kreator publikacji" zawsze wraca na Wejście (brief + lista), nie zostawia głębokiego kroku. */
export const KARUZELE_HOME_STATE = { karuzeleHome: true as const };

type NavItem = { to: string; label: string; icon: LucideIcon };

type NavGroup = {
  label: string;
  icon: LucideIcon;
  /** Tailwind classes for panel, header chip, label, item icon tint */
  tone: {
    panel: string;
    chip: string;
    label: string;
    itemIcon: string;
    itemIconActive: string;
    activeBg: string;
  };
  items: NavItem[];
};

const groups: NavGroup[] = [
  {
    label: "Start",
    icon: Sunrise,
    tone: {
      panel: "border-sky-200/80 bg-sky-50/70",
      chip: "bg-sky-500/15 text-sky-600",
      label: "text-sky-700",
      itemIcon: "text-sky-500/70",
      itemIconActive: "text-sky-600",
      activeBg: "bg-sky-500/15",
    },
    items: [
      { to: "/", label: "Dziś", icon: LayoutDashboard },
      { to: "/podsumowanie", label: "Podsumowanie", icon: Lightbulb },
      { to: "/status", label: "Status", icon: Activity },
    ],
  },
  {
    label: "Wynik",
    icon: TrendingUp,
    tone: {
      panel: "border-emerald-200/80 bg-emerald-50/70",
      chip: "bg-emerald-500/15 text-emerald-600",
      label: "text-emerald-700",
      itemIcon: "text-emerald-500/70",
      itemIconActive: "text-emerald-600",
      activeBg: "bg-emerald-500/15",
    },
    items: [
      { to: "/sprzedaz", label: "Sprzedaż", icon: Wallet },
      { to: "/mailing", label: "Mailing", icon: Mail },
      { to: "/automatyzacje-dm", label: "Automatyzacje DM", icon: MessagesSquare },
      { to: "/landingi", label: "Landingi", icon: LayoutTemplate },
      { to: "/inspiracje", label: "Inspiracje", icon: Lightbulb },
      { to: "/droga-klientki", label: "Droga klientki", icon: Route },
      { to: "/barometr", label: "Barometr marki", icon: Gauge },
    ],
  },
  {
    label: "Treści",
    icon: PenLine,
    tone: {
      panel: "border-brand/25 bg-brand/5",
      chip: "bg-brand/15 text-brand",
      label: "text-brand",
      itemIcon: "text-brand/70",
      itemIconActive: "text-brand",
      activeBg: "bg-brand/15",
    },
    items: [
      { to: "/karuzele", label: "Kreator publikacji", icon: Images },
      { to: "/komentarze", label: "Komentarze", icon: MessageCircle },
      { to: "/publikacje", label: "Publikacje", icon: Newspaper },
      { to: "/inteligencja", label: "Inteligencja", icon: Lightbulb },
      { to: "/rolki", label: "Rolki", icon: Images },
      { to: "/zdjecia", label: "Media", icon: Camera },
      { to: "/elementy", label: "Elementy", icon: PenLine },
      { to: "/fonty", label: "Fonty", icon: Type },
      { to: "/historie-messenger", label: "Historie Messenger", icon: BookOpen },
    ],
  },
  {
    label: "Operacje",
    icon: Wrench,
    tone: {
      panel: "border-amber-200/80 bg-amber-50/70",
      chip: "bg-amber-500/15 text-amber-600",
      label: "text-amber-700",
      itemIcon: "text-amber-500/70",
      itemIconActive: "text-amber-600",
      activeBg: "bg-amber-500/15",
    },
    items: [
      { to: "/wysylka", label: "Wysyłka", icon: Truck },
      { to: "/tydzien-arka", label: "Tydzień Arka", icon: CalendarRange },
      { to: "/plan-tygodnia", label: "Plan tygodnia", icon: CalendarDays },
      { to: "/kanban", label: "Kanban", icon: KanbanSquare },
      { to: "/linki", label: "Linki", icon: Link2 },
      { to: "/interakcje", label: "Interakcje", icon: ClipboardList },
      { to: "/subskrypcje", label: "Subskrypcje", icon: Sparkles },
      { to: "/zespol-ai", label: "Zespół AI", icon: Users },
    ],
  },
  {
    label: "Wiedza",
    icon: BookMarked,
    tone: {
      panel: "border-indigo-200/80 bg-indigo-50/70",
      chip: "bg-indigo-500/15 text-indigo-600",
      label: "text-indigo-700",
      itemIcon: "text-indigo-500/70",
      itemIconActive: "text-indigo-600",
      activeBg: "bg-indigo-500/15",
    },
    items: [
      { to: "/wiedza", label: "Wiedza", icon: Library },
      { to: "/dokumenty", label: "Dokumenty", icon: FileText },
      { to: "/mapy", label: "Mapy myśli", icon: Brain },
      { to: "/bart", label: "Bart", icon: GraduationCap },
      { to: "/przeplyw-wiedzy", label: "Przepływ wiedzy", icon: Network },
    ],
  },
  {
    label: "Lab",
    icon: FlaskConical,
    tone: {
      panel: "border-slate-200/80 bg-slate-50/70",
      chip: "bg-slate-500/15 text-slate-600",
      label: "text-slate-600",
      itemIcon: "text-slate-500/70",
      itemIconActive: "text-slate-600",
      activeBg: "bg-slate-500/15",
    },
    items: [{ to: "/op-demo", label: "OVL demo", icon: Palette }],
  },
];
function isActivePath(pathname: string, to: string): boolean {
  if (to === "/karuzele" || to === "/komentarze" || to === "/dokumenty" || to === "/mapy") {
    return pathname.startsWith(to);
  }
  return pathname === to;
}

export default function Sidebar({
  open = false,
  onNavigate,
}: {
  open?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    window.location.href = "/login";
  };

  const goKaruzeleHome = () => {
    onNavigate?.();
    navigate("/karuzele#brief", { state: { ...KARUZELE_HOME_STATE, t: Date.now() } });
  };

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border/50 bg-card/95 pt-14 shadow-2xl backdrop-blur-xl transition-transform duration-200",
        open ? "translate-x-0" : "-translate-x-full pointer-events-none",
      )}
    >
      <div className="flex items-center gap-3 px-6 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand/60 shadow-lg shadow-brand/30">
          <Sparkles className="h-5 w-5 text-brand-foreground" />
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">Kasia Zaręba</div>
          <div className="text-xs text-muted-foreground">Dashboard v2.0</div>
        </div>
      </div>

      <nav className="flex-1 space-y-2.5 overflow-y-auto px-3 pb-3">
        {groups.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div
              key={group.label}
              className={cn("rounded-xl border p-1.5 shadow-sm", group.tone.panel)}
            >
              <div className="mb-1 flex items-center gap-2 px-2 pt-1">
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-md",
                    group.tone.chip,
                  )}
                  aria-hidden
                >
                  <GroupIcon className="h-3 w-3" />
                </span>
                <span
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider",
                    group.tone.label,
                  )}
                >
                  {group.label}
                </span>
              </div>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon }) => {
                  const active = isActivePath(location.pathname, to);
                  return (
                    <NavLink
                      key={to}
                      to={to === "/karuzele" ? "/karuzele#brief" : to}
                      onClick={(e) => {
                        if (to === "/karuzele") {
                          e.preventDefault();
                          goKaruzeleHome();
                          return;
                        }
                        onNavigate?.();
                      }}
                      className={cn(
                        "relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                        active
                          ? "text-foreground"
                          : "text-muted-foreground hover:bg-white/50 hover:text-foreground",
                      )}
                    >
                      {active && (
                        <motion.div
                          layoutId="sidebar-active"
                          className={cn("absolute inset-0 rounded-lg", group.tone.activeBg)}
                          transition={{ type: "spring", stiffness: 350, damping: 30 }}
                        />
                      )}
                      <Icon
                        className={cn(
                          "relative z-10 h-4 w-4 shrink-0",
                          active ? group.tone.itemIconActive : group.tone.itemIcon,
                        )}
                      />
                      <span className="relative z-10">{label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-border/50 p-3">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
          Wyloguj
        </button>
      </div>
    </aside>
  );
}
